import { ChangeDetectorRef, Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { allColumns, contactColumns, standardColumns } from './column-groups';
import { BehaviorSubject, map, Observable, Subject, takeUntil } from 'rxjs';
import { ContractorListService } from '../../../core/services/contractor-list/contractor-list.service';
import { ContractorListDto } from '../../../core/models/contractors/contractor-list-dto';
import { ContractorListFilter } from '../../../core/models/contractors/contractor-list-filter';
import { ColumnManagerService } from '../../../core/services/column-manager/column-manager.service';
import { MatButtonModule } from '@angular/material/button';
import { ColumnDefinition, ColumnDefinitionContractorList, GenericCellType } from '../../../core/models/column-definition';
import { FilterItem } from '../../../core/models/filter-item';
import * as XLSX from 'xlsx';
import { IconService } from '../../../core/services/icon.service';
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { ContractListFilterComponent } from './contractor-list-filter/contractor-list-filter.component';
import { SortCoordinationService } from '../../../core/services/sort-coordination/sort-coordination.service';
import { MatMenuModule } from '@angular/material/menu';
@Component({
  selector: 'app-contractor-list',
  imports: [MatTableModule, MatPaginatorModule, MatSortModule, RouterModule, MatIconModule, ContractListFilterComponent, MatButtonModule, CommonModule, FontAwesomeModule, MatMenuModule],
  templateUrl: './contractor-list.component.html',
  styleUrl: './contractor-list.component.scss'
})
export class ContractorListComponent {
  private readonly ngUnsubscribe$ = new Subject<void>();
  private allColumns: ColumnDefinition[] = allColumns;
  tableId = 'contractor-table';

  contractors: ContractorListDto[] = [];
  protected filteredContractorList$: Observable<MatTableDataSource<ContractorListDto>> | undefined;
  contractorDataSource = new MatTableDataSource<ContractorListDto>;

  isLoading = true;
  displayedColumns: (string | undefined)[] = [];
  skeletonRows = Array(20).fill(0);

  data$!: BehaviorSubject<ContractorListDto[] | null>;
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;

  combinedColumns: ColumnDefinition[] = [];
  isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private contractorListService: ContractorListService, private columnManagerService: ColumnManagerService, private changeDetectorRef: ChangeDetectorRef, public iconService: IconService, private sortCoordinationService: SortCoordinationService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initSubscriptions = (): void => {
    this.filteredContractorList$ = this.contractorListService.filterChange.pipe(takeUntil(this.ngUnsubscribe$))
      .pipe(
        map((value) => {
          return this.applyFilter(
            value || ""
          );
        })
      );
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      // Avoid backend calls during SSR/prerender.
      this.isLoading = false;
      return;
    }

    this.columnManagerService.registerTable(this.tableId, this.allColumns);

    this.columnManagerService.loadFromStorage(this.tableId);

    this.displayedColumns = this.columnManagerService.getDisplayedColumns(this.tableId);
    this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionContractorList[];

    this.contractorListService.loadContractors();
    this.data$ = this.contractorListService.data$;
    this.data$.subscribe(contractors => {
      if (contractors) {
        this.contractors = contractors;
        this.isLoading = false;
      }
    })
    this.initSubscriptions();

    this.sortCoordinationService.clearSort$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(tableId => {
        if (tableId === this.tableId && this.sort) {
          this.sort.active = '';
          this.sort.direction = '';
          this.columnManagerService.clearSortState(this.tableId);
          this.sort.sortChange.emit({ active: '', direction: '' });
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    this.contractorListService.filterChange.subscribe(filter => {
      this.applyFilter(filter);
      this.changeDetectorRef.detectChanges();
    });

    this.contractorDataSource.paginator = this.paginator!;
    this.contractorDataSource.sort = this.sort!;

    const savedPageSize = this.columnManagerService.loadPageSize(this.tableId);
    if (savedPageSize && this.paginator) {
      this.paginator.pageSize = savedPageSize;
    }

    this.paginator?.page.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((pageEvent) => {
      this.columnManagerService.savePageSize(this.tableId, pageEvent.pageSize);
    });

    const savedSort = this.columnManagerService.loadSortState(this.tableId);
    if (savedSort && this.sort) {
      this.sort.active = savedSort.active;
      this.sort.direction = savedSort.direction;
      this.sort.sortChange.emit({ active: savedSort.active, direction: savedSort.direction });
    }

    this.sort?.sortChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      if (this.sort) {
        this.columnManagerService.saveSortState(this.tableId, this.sort);
      }
    });
  }

  openColumnManager() {
    this.columnManagerService.openColumnManager(this.tableId)
      .subscribe(result => {
        if (result) {

          this.displayedColumns = result.displayedColumns;
          this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionContractorList[];


          this.columnManagerService.saveToStorage(this.tableId);
        }
      });
  }

  applyFilter(filterValues: ContractorListFilter) {
    let contractors = this.data$.value!;

    if (!contractors) {
      return this.contractorDataSource;
    }

    if (filterValues?.cities.length) {
      contractors = contractors.filter((contractor) => this.isPropertyFiltered(filterValues.cities, contractor.city))
    }

    if (filterValues?.statuses.length) {
      contractors = contractors.filter((contractor) => this.isPropertyFiltered(filterValues.statuses, contractor.contractorListStatus))
    }

    const activeSearch = filterValues?.search?.find((item) => item?.activated && !!item?.value?.trim());
    if (activeSearch) {
      contractors = contractors.filter((contractor) => this.checkNestedObject(contractor, activeSearch.value, activeSearch.activated));
    }

    this.contractorDataSource.data = contractors;

    if (this.paginator) this.contractorDataSource.paginator = this.paginator;
    if (this.sort) this.contractorDataSource.sort = this.sort;

    return this.contractorDataSource;
  }

  checkNestedObject = (obj: any, filter: string, activated: boolean = true): boolean => {
    if (!activated) {
      return true;
    }

    if (!filter) {
      return true;
    }

    return Object.keys(obj).some(key => {
      const value = obj[key];
      if (value && typeof value === 'object') {
        return this.checkNestedObject(value, filter);
      }
      const lowerCaseFilter = filter.toLowerCase();
      return value && value.toString().toLowerCase().includes(lowerCaseFilter);
    });
  };

  getProperty = (obj: any, path: any) => (
    path.split('.').reduce((o: any, p: any) => o && o[p], obj)
  )

  getCellValue(value: GenericCellType): GenericCellType {
    const returnedValue = (value !== 'null' && value !== 'undefined') ? value : '-';
    return returnedValue;
  }

  isPropertyFiltered(filterItems: FilterItem[], property: string): boolean {
    return filterItems.every(f => !f.activated) || filterItems.some(f => f.value === property && f.activated);
  }

  exportToExcel() {
    let element = document.getElementById('contractor-list');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element, { raw: true });

    ws['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 10 }, { wch: 20 }];
    ws['A1'].v = 'Org.nr';

    // Formatera alla celler som text för att förhindra datumtolkning
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        if (cell) {
          cell.t = 's';
          cell.z = '@';
        }
      }
    }

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entreprenorer');

    XLSX.writeFile(wb, 'Entreprenorer.xlsx');
  }
}

